/**
 * Canonical role definitions for Carmel Work.
 *
 * Each role has a stable key, display name, department binding,
 * whether it serves as a department head, and default capabilities.
 *
 * Legacy role names (staff, other, management, cao) must be migrated
 * to these canonical keys. They are NOT silently deleted.
 */
import { DEPARTMENTS, type DepartmentId } from "./departments.js";

// ── Role Keys ──────────────────────────────────────────────────────

export const ROLE_KEYS = {
  SUPERADMIN: "superadmin",
  ADMIN: "admin",
  PRINCIPAL: "principal",
  VICE_PRINCIPAL: "vice_principal",
  FACULTY: "faculty",
  LIBRARIAN: "librarian",
  OPERATIONS_MANAGER: "operations_manager",
  FRONT_OFFICE: "front_office",
  ACCOUNTING_ASSISTANT: "accounting_assistant",
  HOSTEL_WARDEN: "hostel_warden",
  OPERATIONS_SUPPORT: "operations_support",
  ACCOUNTANT: "accountant",
} as const;

export type RoleKey = (typeof ROLE_KEYS)[keyof typeof ROLE_KEYS];

/** Canonical role keys that are global business-owner roles (not department-bound). */
export const GLOBAL_OWNER_ROLES: readonly RoleKey[] = [
  ROLE_KEYS.SUPERADMIN,
  ROLE_KEYS.ADMIN,
];

/** Canonical role keys that serve as department heads. */
export const DEPARTMENT_HEAD_ROLES: readonly RoleKey[] = [
  ROLE_KEYS.PRINCIPAL,
  ROLE_KEYS.OPERATIONS_MANAGER,
];

// ── Role Definitions ───────────────────────────────────────────────

export interface RoleDefinition {
  key: RoleKey;
  displayName: string;
  /** Department this role belongs to. null for global owner roles. */
  departmentId: DepartmentId | null;
  /** Whether this role serves as a department head. */
  isDepartmentHead: boolean;
  /** Default capabilities granted to this role. */
  capabilities: readonly string[];
}

/**
 * All canonical role definitions with their default capabilities.
 * Capabilities follow the dot-separated naming convention from the plan.
 */
export const ROLE_DEFINITIONS: readonly RoleDefinition[] = [
  // ── Global business-owner roles ──
  {
    key: ROLE_KEYS.SUPERADMIN,
    displayName: "Superadmin",
    departmentId: null,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.organisation",
      "task.view.all",
      "task.create",
      "task.assign.all",
      "task.update.all",
      "task.reassign.all",
      "task.cancel.all",
      "approval.manage.all",
      "analytics.view.organisation",
      "report.view.all",
      "organisation.manage",
      "user.manage",
      "role.manage",
      "audit.view",
      "intervention.manage.organisation",
    ],
  },
  {
    key: ROLE_KEYS.ADMIN,
    displayName: "Admin",
    departmentId: null,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.organisation",
      "task.view.all",
      "task.create",
      "task.assign.all",
      "task.update.all",
      "task.reassign.all",
      "task.cancel.all",
      "approval.manage.all",
      "analytics.view.organisation",
      "report.view.all",
      "organisation.manage",
      "user.manage",
      "role.manage",
      "audit.view",
      "intervention.manage.organisation",
    ],
  },

  // ── Academics department ──
  {
    key: ROLE_KEYS.PRINCIPAL,
    displayName: "Principal",
    departmentId: DEPARTMENTS.ACADEMICS,
    isDepartmentHead: true,
    capabilities: [
      "dashboard.view.department",
      "task.view.own",
      "task.view.department",
      "task.create",
      "task.assign.self",
      "task.assign.department",
      "task.assign.cross_department",
      "task.update.execution",
      "task.update.department",
      "approval.request",
      "approval.manage.department",
      "analytics.view.department",
      "report.view.department",
    ],
  },
  {
    key: ROLE_KEYS.VICE_PRINCIPAL,
    displayName: "Vice Principal",
    departmentId: DEPARTMENTS.ACADEMICS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
      "approval.request",
    ],
  },
  {
    key: ROLE_KEYS.FACULTY,
    displayName: "Faculty",
    departmentId: DEPARTMENTS.ACADEMICS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
    ],
  },
  {
    key: ROLE_KEYS.LIBRARIAN,
    displayName: "Librarian",
    departmentId: DEPARTMENTS.ACADEMICS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
    ],
  },

  // ── Operations department ──
  {
    key: ROLE_KEYS.OPERATIONS_MANAGER,
    displayName: "Operations Manager",
    departmentId: DEPARTMENTS.OPERATIONS,
    isDepartmentHead: true,
    capabilities: [
      "dashboard.view.department",
      "task.view.own",
      "task.view.department",
      "task.create",
      "task.assign.self",
      "task.assign.department",
      "task.update.execution",
      "task.update.department",
      "approval.request",
      "approval.manage.department",
      "analytics.view.department",
      "report.view.department",
    ],
  },
  {
    key: ROLE_KEYS.FRONT_OFFICE,
    displayName: "Front Office",
    departmentId: DEPARTMENTS.OPERATIONS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
    ],
  },
  {
    key: ROLE_KEYS.ACCOUNTING_ASSISTANT,
    displayName: "Accounting Assistant",
    departmentId: DEPARTMENTS.OPERATIONS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
    ],
  },
  {
    key: ROLE_KEYS.HOSTEL_WARDEN,
    displayName: "Hostel Warden",
    departmentId: DEPARTMENTS.OPERATIONS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
    ],
  },
  {
    key: ROLE_KEYS.OPERATIONS_SUPPORT,
    displayName: "Operations Support",
    departmentId: DEPARTMENTS.OPERATIONS,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.create",
      "task.assign.self",
      "task.update.execution",
    ],
  },

  // ── Finance department ──
  {
    key: ROLE_KEYS.ACCOUNTANT,
    displayName: "Accountant",
    departmentId: DEPARTMENTS.FINANCE,
    isDepartmentHead: false,
    capabilities: [
      "dashboard.view.personal",
      "task.view.own",
      "task.update.execution",
      "approval.request",
    ],
  },
] as const;

// ── Role Lookup Helpers ────────────────────────────────────────────

/** Lookup a role definition by its key. */
export function getRoleByKey(key: string): RoleDefinition | undefined {
  return ROLE_DEFINITIONS.find((r) => r.key === key);
}

/** Check if a role key represents a global business-owner role. */
export function isGlobalOwnerRole(key: string): boolean {
  return GLOBAL_OWNER_ROLES.includes(key as RoleKey);
}

/** Check if a role key represents a department head. */
export function isDepartmentHead(key: string): boolean {
  return DEPARTMENT_HEAD_ROLES.includes(key as RoleKey);
}

/** Get all role keys. */
export function getAllRoleKeys(): RoleKey[] {
  return ROLE_DEFINITIONS.map((r) => r.key);
}

/** Get roles belonging to a specific department. */
export function getRolesByDepartment(departmentId: string): RoleDefinition[] {
  return ROLE_DEFINITIONS.filter((r) => r.departmentId === departmentId);
}

/** Get the department ID for a role key. */
export function getDepartmentForRole(roleKey: string): DepartmentId | null {
  return getRoleByKey(roleKey)?.departmentId ?? null;
}

/**
 * Map legacy role names to canonical role keys.
 * This is used during migration. Unknown roles return null.
 */
export const LEGACY_ROLE_MAP: Record<string, RoleKey> = {
  superadmin: ROLE_KEYS.SUPERADMIN,
  admin: ROLE_KEYS.ADMIN,
  management: ROLE_KEYS.ADMIN, // management -> admin (closest equivalent)
  cao: ROLE_KEYS.OPERATIONS_MANAGER, // cao -> operations_manager
  staff: ROLE_KEYS.FACULTY, // staff -> faculty
  other: ROLE_KEYS.FACULTY, // other -> faculty
  principal: ROLE_KEYS.PRINCIPAL,
  vice_principal: ROLE_KEYS.VICE_PRINCIPAL,
  faculty: ROLE_KEYS.FACULTY,
  librarian: ROLE_KEYS.LIBRARIAN,
  operations_manager: ROLE_KEYS.OPERATIONS_MANAGER,
  hostel_warden: ROLE_KEYS.HOSTEL_WARDEN,
  accountant: ROLE_KEYS.ACCOUNTANT,
  front_office: ROLE_KEYS.FRONT_OFFICE,
  accounting_assistant: ROLE_KEYS.ACCOUNTING_ASSISTANT,
  operations_support: ROLE_KEYS.OPERATIONS_SUPPORT,
};

/** Resolve a legacy role name to a canonical role key. Returns null if unknown. */
export function resolveLegacyRole(legacyRole: string): RoleKey | null {
  return LEGACY_ROLE_MAP[legacyRole] ?? null;
}
